import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs/Observable';

import { plainToClass } from 'class-transformer';

import { SnackBarService } from 'app/services/snackbar.service';
import { MessageService } from 'app/services/messages.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { ApplicationTypeConstants, WarmUpService } from 'app/services/warmup/warmup.service';
import { FormErrors } from 'app/shared/validators/generic-validators';

import { Person } from 'app/shared/models/person.model';

import { PersonHeaderService } from './../person-header.service';

@Component({
    selector: 'app-update-person',
    templateUrl: './update-person.component.html',
    styleUrls: ['./update-person.component.css']
})

/**
 * Component to update person details.
 */
export class UpdatePersonComponent implements OnInit {
    personForm: FormGroup;
    personDetails: Person;
    savingPerson: boolean;
    gender: ApplicationValue[];
    nationality: ApplicationValue[];
    nationalityDropDownValues: any[] = [];

    formErrors = {
        'firstNames': '',
        'lastNames': '',
        'callingNames': '',
        'gender': '',
        'nationality': '',
    };

    validationMessages = {
        'firstNames': {
            'required': 'Required field value.',
        },
        'lastNames': {
            'required': 'Required field value.',
        },
        'callingName': {
            'required': 'Required field value.',
        },
        'gender': {
            'required': 'Required field value.',
        },
        'nationality': {
            'required': 'Required field value.',
        },
    };

    constructor(
        public dialogRef: MdDialogRef<any>,
        private formBuilder: FormBuilder,
        private personHeaderService: PersonHeaderService,
        private snackBarService: SnackBarService,
        private warmUpService: WarmUpService,
        private cdr: ChangeDetectorRef) { }

    ngOnInit() {
        this.createForm();
        this.updatePersonForm();
        this.setUpGender(this.warmUpService.getApplicationValue(ApplicationTypeConstants.GENDER_TYPE));
        this.setUpNationality(this.warmUpService.getApplicationValue(ApplicationTypeConstants.COUNTRY_TYPE));
    }

    setUpGender(dropDownValues: any) {
        if (dropDownValues instanceof Observable) {
            dropDownValues.subscribe(
                response => {
                    this.warmUpService.setApplicationTypeInLS(response);
                    this.gender = response.values;
                    this.cdr.detectChanges();
                }
            );
        } else {
            this.gender = dropDownValues;
        }
    }

    setUpNationality(dropDownValues: any) {
        if (dropDownValues instanceof Observable) {
            dropDownValues.subscribe(
                response => {
                    this.warmUpService.setApplicationTypeInLS(response);
                    this.nationality = response.values;
                    this.cdr.detectChanges();
                }
            );
        } else {
            this.nationality = dropDownValues;
        }
        // TODO(naveenkiran): Adding default entry to the dropdown list as placeholder value is
        // not getting set. Remove this once we use dropdown component of ng-material library.
        this.nationalityDropDownValues.push({
            'value': '',
            'label': 'Select country *' // >> most simple fix to show 'required' in p-dropdown
        });
        for (let countryKey in this.nationality) {
            let countryRecord = this.nationality[countryKey];
            this.nationalityDropDownValues.push({
                'value': countryRecord.id,
                'label': countryRecord.name
            });
        }
    }

    createForm() {
        this.personForm = this.formBuilder.group({
            firstNames: ['', Validators.required],
            lastNames: ['', Validators.required],
            middleNames: [''],
            callingName: ['', Validators.required],
            ssn: [''],
            gender: ['', Validators.required],
            nationality: ['', Validators.required],
            birthday: [''],
        });
        this.personForm.valueChanges.subscribe(data => this.onValueChanged(data));
        this.onValueChanged();
    }

    onValueChanged(data?: any) {
        if (!this.personForm) {
            return;
        }
        FormErrors('personForm', this);
    }

    updatePersonForm(): void {
        if (this.personDetails) {
            this.personForm.patchValue({
                firstNames: this.personDetails.firstNames,
                lastNames: this.personDetails.lastNames,
                middleNames: this.personDetails.middleNames,
                callingName: this.personDetails.callingName,
                ssn: this.personDetails.ssn,
                gender: this.personDetails.gender,
                nationality: this.personDetails.nationality,
                birthday: this.formatBirthday(this.personDetails.birthday),
            });
        }
    }

    onSubmit({ value, valid }) {
        if (!value.nationality || value.nationality == -1) {
            this.formErrors['nationality'] = this.validationMessages.nationality.required;
            return;
        }
        this.savingPerson = true;
        const updatedPersonDetails = new Person();
        updatedPersonDetails.id = this.personDetails.id;
        updatedPersonDetails.firstNames = value.firstNames;
        updatedPersonDetails.lastNames = value.lastNames;
        updatedPersonDetails.middleNames = value.middleNames;
        updatedPersonDetails.callingName = value.callingName;
        updatedPersonDetails.gender = value.gender;
        updatedPersonDetails.genderName = this.filterGenderById(value.gender);
        updatedPersonDetails.nationality = value.nationality;
        updatedPersonDetails.nationalityName = this.filterNationalityById(value.nationality);
        updatedPersonDetails.birthday = this.formatBirthday(value.birthday) || null;
        updatedPersonDetails.ssn = value.ssn;
        this.personHeaderService
            .updatePerson(updatedPersonDetails)
            .subscribe(response => {
                this.savingPerson = false;
                if (response.flagResult) {
                    this.dialogRef.close(response.arrResult);
                }
                this.callSnackBarService(response,
                    MessageService.UPDATE_PERSON_SUCCESS,
                    MessageService.UPDATE_PERSON_FAILURE);
            },
            err => {
                this.savingPerson = false;
                this.callSnackBarService(err,
                    MessageService.UPDATE_PERSON_SUCCESS,
                    MessageService.UPDATE_PERSON_FAILURE);
            });
    }

    callSnackBarService(response: any, successMsg: string, failureMsg: string) {
        this.snackBarService.showStatus(response, successMsg, failureMsg);
    }

    /**
     * Jackson parses birthday as yyyy-MM-dd and rejects unpadded values such as 1970-1-1.
     * A missing birthday stays empty; new Date(null) would otherwise become 1970-01-01.
     */
    private formatBirthday(value: any): string {
        if (value == null || value === '') {
            return '';
        }
        if (typeof value === 'string') {
            const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value.trim());
            if (match) {
                const month = ('0' + match[2]).slice(-2);
                const day = ('0' + match[3]).slice(-2);
                return match[1] + '-' + month + '-' + day;
            }
        }
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date.getTime())) {
            return '';
        }
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const day = ('0' + date.getDate()).slice(-2);
        return date.getFullYear() + '-' + month + '-' + day;
    }

    filterGenderById(genderId: number): string {
        if (this.gender) {
            return this.gender
                .find(gender => gender.id === genderId).name;
        }
    }

    filterNationalityById(nationalityId: number): string {
        if (this.nationality) {
            return this.nationality
                .find(nationality => nationality.id === nationalityId).name;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/person/header/updateperson/update-person.component.ts